import agent from '@root/agent';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
import useDebouncedValue from '@root/hooks/useDebouncedValue';
import { useQuery } from '@root/hooks/useQuery';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import { axiosHelper } from '@root/services/axios';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Building,
  Check,
  ChevronLeft,
  ChevronRight,
  GitBranch,
  Info,
  Loader,
  Search,
  // PieChart,
  Server,
  // SettingsIcon,
  X,
} from 'lucide-react';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import './styles.scss';
// import Loader from '@root/components/common/Loader';
import DiscordLogo from '@assets/img/notifications/discord-icon.svg';
import EmailLogo from '@assets/img/notifications/form/email.png';
import PubsubLogo from '@assets/img/notifications/form/pubsub.png';
import SlackLogo from '@assets/img/notifications/form/slack.png';
import SNSLogo from '@assets/img/notifications/form/sns.png';
import GoogleChatLogo from '@assets/img/notifications/google-chat.png';
import TeamsLogo from '@assets/img/notifications/microsoft-teams.png';
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import Switch from '@mui/material/Switch';
import shieldRoundIcon from '@root/assets/svg/shieldIcon.svg';
// import Select from '@root/components/v2/ui-elements/Select';
import usePubSub from '@root/hooks/usePubSub';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import GroupSelect from './_components/GroupSelect';
import SimpleYamlViewer from './_components/SimpleYamlViewer';
import { Spinner } from 'react-bootstrap';

const channels = [
  {
    id: 'INAPP',
    name: (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <img src={shieldRoundIcon} alt="logo" style={{ width: '1.9rem' }} />{' '}
        <span style={{ marginLeft: 5, fontSize: 12, fontWeight: 600 }}>InApp</span>
      </span>
    ),
  },
  {
    id: 'EMAIL',
    name: (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <img src={EmailLogo} style={{ width: '1.3rem' }} alt="logo" />{' '}
        <span style={{ marginLeft: 5, fontSize: 12, fontWeight: 600 }}>Email</span>
      </span>
    ),
  },
  {
    id: 'SLACK',
    name: (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <img src={SlackLogo} style={{ width: '5rem' }} alt="logo" />
      </span>
    ),
  },
  {
    id: 'Sns',
    name: (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <img src={SNSLogo} style={{ width: '4rem' }} alt="logo" />
      </span>
    ),
  },
  {
    id: 'Pubsub',
    name: (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <img src={PubsubLogo} alt="logo" style={{ width: '1.9rem' }} />
        <span style={{ marginLeft: 5, fontSize: 12, fontWeight: 600 }}>Cloud Pub/Sub</span>
      </span>
    ),
  },
  {
    id: 'MICROSOFTTEAMS',
    name: (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <img src={TeamsLogo} alt="logo" style={{ width: '1.45rem' }} />{' '}
        <span style={{ marginLeft: 5, fontSize: 12, fontWeight: 600 }}>Microsoft teams</span>
      </span>
    ),
  },
  {
    id: 'GOOGLECHAT',
    name: (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <img src={GoogleChatLogo} alt="logo" style={{ width: '1.6rem' }} />{' '}
        <span style={{ marginLeft: 5, fontSize: 12, fontWeight: 600 }}>Google chat</span>
      </span>
    ),
  },
  {
    id: 'DISCORD',
    name: (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <img src={DiscordLogo} alt="logo" style={{ width: '1.9rem' }} />{' '}
        <span style={{ marginLeft: 5, fontSize: 12, fontWeight: 600 }}>Discord</span>
      </span>
    ),
  },
];

const typographyStyles = {
  fontSize: '14px',
  fontWeight: 500,
  color: '#1A202C',
  display: 'inline-block',
  marginLeft: '4px',
  fontFamily: 'Manrope',
};

const severityOptions = [
  {
    label: 'Critical',
    value: 'Critical',
  },
  {
    label: 'High',
    value: 'High',
  },
  {
    label: 'Medium',
    value: 'Medium',
  },
  {
    label: 'Low',
    value: 'Low',
  },
];

const secretStatusOptions = [
  {
    label: 'Active',
    value: 'Active',
  },
  {
    label: 'Inactive',
    value: 'Inactive',
  },
];

export const IacOnboarding = ({ repoDetails, editMode = false, onClose }) => {
  // Shared state

  const query = useQuery();
  const [currentStep, setCurrentStep] = useState(1); // Always start at step 1, even in edit mode

  // Step 1: Repository Selection state
  const [selectedRepos, setSelectedRepos] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  // Step 2: Compliance Selection state
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [activeTab, setActiveTab] = useState('organization');
  const [organization, setOrganization] = useState({ name: 'Seclogic', defaultFrameworks: [] });
  const [filter, setFilter] = useState('all');
  const [selectedRepo, setSelectedRepo] = useState(null);

  const [isValidatingBranches, setIsValidatingBranches] = useState(false);
  const [isRunningScan, setIsRunningScan] = useState(false);
  const [isEnableApi, setIsEnableApi] = useState(false);
  const token = query.get('code');
  const state = query.get('state');

  const installation_id = query.get('installation_id') ?? query.get('provider');
  const history = useHistory();
  const searchTextDebounced = useDebouncedValue(searchTerm, 750);

  const [integrationType, setIntegrationType] = useState('Manual');
  const [cidcdType, setcicdType] = useState('');
  const [selectedchannel, setSelectedChannel] = useState(['INAPP']);

  const [isSlackEnabled, setIsSlackEnabled] = useState(false);
  const [isInAppEnabled, setIsInAppEnabled] = useState(false);
  const [isSlackConfigured, setIsSlackConfigured] = useState(false);
  const [isMicrosoftTeamsEnabled, setIsMicrosoftTeamsEnabled] = useState(false);
  const [isMicrosoftTeamsConfigured, setIsMicrosoftTeamsConfigured] = useState(false);
  const [isGoogleChatEnabled, setIsGoogleChatEnabled] = useState(false);
  const [isGoogleChatConfigured, setIsGoogleChatConfigured] = useState(false);
  const [isDiscordEnabled, setIsDiscordEnabled] = useState(false);
  const [isDiscordConfigured, setIsDiscordConfigured] = useState(false);
  const [groups, setGroups] = useState([
    {
      secret: ['Active', 'Inactive'],
      cve: ['Critical'],
      iac: ['Critical'],
      repos: '',
    },
  ]);

  const [isLoadingNextButton, setIsLoadingNextButton] = useState(false);
  const [isScanRunning, setIsScanRunning] = useState(false);
  const [yamlContent, setYamlContent] = useState('');

  // console.log(repoDetails, 'selectedchannel');

  const [ciCICDPage, setCICDPage] = useState(1);

  const onChannelSelect = element => {
    if (selectedchannel?.includes(element)) {
      setSelectedChannel(prev => prev?.filter(item => item != element));
    } else {
      setSelectedChannel(prev => [...prev, element]);
    }
  };

  usePubSub(
    'Repository_insert',
    data => {
      if (!data || !data.status === 'updated') return;
      setIsEnableApi(true);
    },
    [isEnableApi],
  );
  // API Queries
  const { data: selectedCompliances } = useEnhancedQuery(
    ['get_selected_compliances'],
    () =>
      agent.CompliancePacks.get_selected_compliances({
        environment_id: [],
        cloud_provider: 'aws',
      }),
    { cacheTime: 0, staleTime: 0 },
  );

  const { data: complianceFramework, isFetching: isFetchingComplianceFrameworks } = useEnhancedQuery(
    ['get_list_compliance_framework', currentPage, searchTerm, editMode, repoDetails],
    () =>
      agent.repoScan.get_list_compliance_framework({
        provider: editMode ? repoDetails?.provider : installation_id ? 'github' : 'bitbucket',
      }),
  );
  console.log(complianceFramework, isFetchingComplianceFrameworks, 'lsd');
  const { data: tableData, isFetching: isFetchingTableData } = useEnhancedQuery(
    ['get_list_repo_iac_onboarding', currentPage, searchTerm, isEnableApi, editMode, repoDetails],
    () =>
      agent.repoScan.get_list_repo({
        platform: editMode ? repoDetails?.provider : installation_id ? 'github' : 'bitbucket',
        page_no: currentPage,
        row_per_page: 5,
        search_keyword: searchTerm,
      }),
    {
      enabled: isEnableApi,
    },
  );

  // Helper function to get compliance descriptions
  const getComplianceDescription = complianceName => {
    const descriptions = {
      'AWS-Well-Architected': 'Best practices for AWS architecture',
      'CIS-AWS': 'Center for Internet Security AWS configuration guidelines',
      'NIST-800-53': 'Security controls for federal information systems',
      'ISO-27001': 'International standard for information security',
      'CSA-CCM': 'Cloud Security Alliance Cloud Controls Matrix',
      'PCI-DSS': 'Payment Card Industry Data Security Standard',
      GDPR: 'General Data Protection Regulation',
      'SOC-2': 'Service Organization Control 2 compliance',
      POPI_Act: 'Protection of Personal Information Act',
      HIPAA: 'Health Insurance Portability and Accountability Act',
      FedRAMP: 'Federal Risk and Authorization Management Program',
      CMMC: 'Cybersecurity Maturity Model Certification',
      FISMA: 'Federal Information Security Modernization Act',
      NYDFS: 'New York Department of Financial Services',
      AWS_FSBP: 'AWS Financial Services Business Partner',
      SAMA_CSF: 'Saudi Arabian Monetary Authority Cybersecurity Framework',
      CBB: 'Central Bank of Bahrain',
      RBI_CSF: 'Reserve Bank of India Cybersecurity Framework',
      NIST_CSF: 'NIST Cybersecurity Framework',
    };

    const baseName = complianceName.split('_')[0];
    return descriptions[baseName] || `${baseName} compliance framework`;
  };

  // Add state for complianceFrameworks
  const [complianceFrameworks, setComplianceFrameworks] = useState([]);

  // Update complianceFrameworks when selectedCompliances changes
  useEffect(() => {
    if (selectedCompliances?.data) {
      const recommendedIds = ['cis', 'nist', 'hipaa', 'aws-wa'];
      const transformedData = selectedCompliances.data.map(compliance => ({
        id: compliance.compliance_name,
        name: compliance.compliance_name,
        description: getComplianceDescription(compliance.compliance_name),
        recommended:
          recommendedIds.includes(compliance.compliance_name) ||
          compliance.compliance_name === 'AWS-Well-Architected_v2020-07-02',
        isSelected: compliance.is_selected,
      }));
      setComplianceFrameworks(transformedData);
    }
  }, [selectedCompliances]);

  // Effects
  useEffect(() => {
    if (searchTextDebounced) {
      // Do not remove. Keep for triggering effect on debouncedSearchString.
    }
    setCurrentPage(1);
  }, [searchTextDebounced, setCurrentPage]);

  useEffect(() => {
    if (complianceFrameworks.length > 0 && !expandedCategory) {
      setExpandedCategory(complianceFrameworks[0].id);
    }
  }, [complianceFrameworks, expandedCategory]);

  useEffect(() => {
    if (token && state && !installation_id) {
      axiosHelper.axiosPost(
        'cloudsecurity/iac/oauth_callback',
        {
          provider: 'bitbucket',
          code: token,
          state: state,
        },
        true,
      );
    } else if (token && state && installation_id) {
      axiosHelper.axiosPost(
        'cloudsecurity/iac/oauth_callback',
        {
          provider: 'github',
          code: token,
          state: state,
        },
        true,
      );
    }
  }, [installation_id, state, token]);

  // Initialize selected repository in edit mode
  useEffect(() => {
    if (editMode && repoDetails) {
      // Initialize selectedRepos with the repo to edit
      setSelectedRepos({
        [repoDetails.full_name]: {
          repoId: repoDetails.full_name,
          repoName: repoDetails.name,
          fullName: repoDetails.full_name,
          platform: repoDetails.provider,
          branches: repoDetails.branch.map(branch => ({
            name: branch,
            isValid: true,
          })),
          currentInput: '',
          isValid: true,
          frameworks: repoDetails.compliance.filter(Boolean), // Filter out null values
        },
      });

      // Set the selected repo
      setSelectedRepo({
        repoId: repoDetails.full_name,
        repoName: repoDetails.name,
        fullName: repoDetails.full_name,
        platform: repoDetails.provider,
        branches: repoDetails.branch.map(branch => ({
          name: branch,
          isValid: true,
        })),
        frameworks: repoDetails.compliance.filter(Boolean),
      });

      // Initialize repoFrameworks with the repo's compliance
      setRepoFrameworks({
        [repoDetails.full_name]: repoDetails.compliance.filter(Boolean),
      });

      // In edit mode, set the organization default frameworks to the repository's compliance
      setOrganization(prev => ({
        ...prev,
        defaultFrameworks: repoDetails.compliance.filter(Boolean),
      }));

      // Update complianceFrameworks to reflect the selected state
      if (selectedCompliances?.data) {
        const frameworks = selectedCompliances.data.map(compliance => ({
          id: compliance.compliance_name,
          name: compliance.compliance_name,
          description: getComplianceDescription(compliance.compliance_name),
          recommended: compliance.compliance_name === 'AWS-Well-Architected_v2020-07-02',
          isSelected: repoDetails.compliance.includes(compliance.compliance_name),
        }));
        setComplianceFrameworks(frameworks);
      }
    }
  }, [editMode, repoDetails, selectedCompliances]);

  // Handle page change
  const handlePageChange = newPage => {
    setCurrentPage(newPage);
  };

  // Initialize
  useEffect(() => {
    // Initialize expanded category
    if (complianceFrameworks.length > 0 && !expandedCategory) {
      setExpandedCategory(complianceFrameworks[0].id);
    }
  }, [complianceFrameworks, expandedCategory]);

  // Get current repos for pagination

  // Toggle repo selection
  const toggleRepoSelection = repo => {
    setSelectedRepos(prev => {
      const newState = { ...prev };
      if (newState[repo.id]) {
        delete newState[repo.id];
        // Also remove from repoFrameworks
        setRepoFrameworks(prev => {
          const updated = { ...prev };
          delete updated[repo.id];
          return updated;
        });
      } else {
        newState[repo.id] = {
          repoId: repo.id,
          repoName: repo.name,
          platform: repo.platform,
          branches: [],
          currentInput: '',
          isValid: false,
          frameworks: [], // Initialize with empty frameworks array
        };
        // Initialize repoFrameworks for this repo
        setRepoFrameworks(prev => ({
          ...prev,
          [repo.id]: [],
        }));
      }
      return newState;
    });
  };

  // Add a branch name to the selected repo
  const addBranchName = (repoId, branchName) => {
    if (!branchName.trim()) return;

    setSelectedRepos(prev => {
      // Don't add if already at 5 branches
      if (prev[repoId].branches.length >= 5) {
        updateBranchValidationMessage(repoId, 'Maximum 5 branches allowed');
        return prev;
      }

      // Check for duplicates
      if (prev[repoId].branches.some(b => b.name === branchName)) {
        updateBranchValidationMessage(repoId, `Branch "${branchName}" already exists`);
        return prev;
      }

      const isValid = validateBranchName(branchName, prev[repoId].platform);
      const newBranches = [...prev[repoId].branches, { name: branchName, isValid }];

      // Clear validation message when adding a valid branch
      if (isValid) {
        updateBranchValidationMessage(repoId, '');
      } else {
        updateBranchValidationMessage(repoId, `Invalid branch name: ${branchName}`);
      }

      return {
        ...prev,
        [repoId]: {
          ...prev[repoId],
          branches: newBranches,
          currentInput: '',
          isValid: newBranches.length > 0 && newBranches.every(b => b.isValid),
        },
      };
    });
  };

  // Remove a branch from a repo
  const removeBranch = (repoId, branchName) => {
    setSelectedRepos(prev => {
      const newBranches = prev[repoId].branches.filter(b => b.name !== branchName);
      const newState = {
        ...prev,
        [repoId]: {
          ...prev[repoId],
          branches: newBranches,
          isValid: newBranches.length > 0 && newBranches.every(b => b.isValid),
        },
      };

      // Update validation message when removing branches
      if (newBranches.length === 0) {
        updateBranchValidationMessage(repoId, 'Please select at least one branch');
      } else if (newBranches.every(b => b.isValid)) {
        // Clear validation message if all remaining branches are valid
        updateBranchValidationMessage(repoId, '');
      } else {
        // Update validation message to show remaining invalid branches
        const invalidBranches = newBranches.filter(b => !b.isValid).map(b => b.name);
        if (invalidBranches.length > 0) {
          updateBranchValidationMessage(repoId, `Invalid branch names: ${invalidBranches.join(', ')}`);
        } else {
          updateBranchValidationMessage(repoId, '');
        }
      }

      return newState;
    });
  };

  // Update current input value for a repo
  const updateCurrentInput = (repoId, value) => {
    setSelectedRepos(prev => ({
      ...prev,
      [repoId]: {
        ...prev[repoId],
        currentInput: value,
      },
    }));
  };

  // Validate branch name based on platform
  const validateBranchName = (branchName, platform) => {
    if (!branchName.trim()) return false;

    // Basic validation rules per platform
    switch (platform) {
      case 'github':
        // GitHub branch name cannot contain: ~, ^, :, \, ?, *, [, space
        return !/[~^:\\?*[\s]/.test(branchName);
      case 'bitbucket':
        // Bitbucket branch name cannot contain: ~, ^, :, \, ?, *, [, space
        return !/[~^:\\?*[\s]/.test(branchName);
      case 'gitlab':
        // GitLab branch name cannot contain: ~, ^, :, \, ?, *, [, space, @{
        return !/[~^:\\?*[\s@{]/.test(branchName);
      default:
        return true;
    }
  };

  // Handle key down for branch input
  const handleKeyDown = (e, repoId) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const value = selectedRepos[repoId].currentInput.trim();
      if (value) {
        addBranchName(repoId, value);
      }
    }
  };

  // Check if repo selection submit is enabled (step 1)
  const isRepoSubmitEnabled = () => {
    return (
      Object.values(selectedRepos).length > 0 &&
      Object.values(selectedRepos).every(
        repo => repo.branches.length > 0 && repo.branches.every(branch => branch.isValid),
      )
    );
  };

  // Toggle compliance selection
  // const toggleCompliance = (frameworkId) => {
  //   setSelectedCompliance(prev => {
  //     const newSelection = { ...prev };
  //     if (newSelection[frameworkId]) {
  //       delete newSelection[frameworkId];
  //     } else {
  //       newSelection[frameworkId] = true;
  //     }
  //     return newSelection;
  //   });
  // };

  // // Toggle category expansion
  // const toggleCategory = (category) => {
  //   setExpandedCategory(prev => prev === category ? null : category);
  // };

  // Get framework by ID

  // Toggle organization framework selection
  const toggleOrgFramework = frameworkId => {
    setOrganization(prev => {
      const updatedDefaults = [...prev.defaultFrameworks];
      const wasSelected = updatedDefaults.includes(frameworkId);
      if (wasSelected) {
        const index = updatedDefaults.indexOf(frameworkId);
        updatedDefaults.splice(index, 1);
      } else {
        updatedDefaults.push(frameworkId);
      }

      // If deselecting a default, remove it from all repo-level frameworks
      if (wasSelected) {
        setRepoFrameworks(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(repoId => {
            updated[repoId] = (updated[repoId] || []).filter(f => f !== frameworkId);
          });
          return updated;
        });

        setSelectedRepos(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(repoId => {
            updated[repoId].frameworks = (updated[repoId].frameworks || []).filter(f => f !== frameworkId);
          });
          return updated;
        });
      }

      return {
        ...prev,
        defaultFrameworks: updatedDefaults,
      };
    });

    // Update complianceFrameworks state
    setComplianceFrameworks(prev =>
      prev.map(framework =>
        framework.id === frameworkId ? { ...framework, isSelected: !framework.isSelected } : framework,
      ),
    );
  };

  // Add state for repo frameworks
  const [repoFrameworks, setRepoFrameworks] = useState({});

  // Toggle repository framework selection
  const toggleRepoFramework = (repoId, frameworkId) => {
    // Check if it's a default framework
    const isDefaultFramework = organization.defaultFrameworks.includes(frameworkId);

    // If it's a default framework, don't allow toggling
    if (isDefaultFramework) {
      return;
    }

    // Update repoFrameworks state
    setRepoFrameworks(prev => {
      const updatedFrameworks = { ...prev };
      if (!updatedFrameworks[repoId]) {
        updatedFrameworks[repoId] = [];
      }

      const frameworks = [...updatedFrameworks[repoId]];

      // Toggle the framework
      if (frameworks.includes(frameworkId)) {
        const index = frameworks.indexOf(frameworkId);
        frameworks.splice(index, 1);
      } else {
        frameworks.push(frameworkId);
      }

      updatedFrameworks[repoId] = frameworks;
      return updatedFrameworks;
    });

    // Update selectedRepos state
    setSelectedRepos(prev => {
      const updatedRepos = { ...prev };
      const repo = updatedRepos[repoId];

      // Initialize frameworks array if it doesn't exist
      if (!repo.frameworks) {
        repo.frameworks = [];
      }

      // Create a new frameworks array
      const frameworks = [...repo.frameworks];

      // Toggle the framework
      if (frameworks.includes(frameworkId)) {
        const index = frameworks.indexOf(frameworkId);
        frameworks.splice(index, 1);
      } else {
        frameworks.push(frameworkId);
      }

      // Update the repo with new frameworks
      updatedRepos[repoId] = {
        ...repo,
        frameworks,
      };

      // Update selectedRepo state if it's the current repo
      if (selectedRepo && selectedRepo.repoId === repoId) {
        setSelectedRepo({
          ...selectedRepo,
          frameworks,
        });
      }

      return updatedRepos;
    });
  };
  // console.log(currentStep, 'currentStep');
  // Check if submit button for compliance should be enabled
  const isComplianceSubmitEnabled = () => {
    // Check if organization has default frameworks selected
    const hasOrgDefaults = organization.defaultFrameworks.length > 0;

    // If org defaults are selected, all repos will inherit them, so button should be enabled
    if (hasOrgDefaults) {
      return true;
    }

    // If no org defaults, check if all repos have at least one framework selected
    const allReposHaveFrameworks = Object.values(selectedRepos).every(repo => {
      // Get frameworks for this repo
      const repoSpecificFrameworks = repoFrameworks[repo.repoId] || [];
      return repoSpecificFrameworks.length > 0;
    });

    return allReposHaveFrameworks;
  };

  // Filter frameworks based on selection
  const getFilteredFrameworks = () => {
    if (filter === 'all') return complianceFrameworks;
    if (filter === 'recommended') return complianceFrameworks.filter(f => f.recommended);
    return complianceFrameworks;
  };

  // Success message function (disabled as requested)

  // Navigate to next step
  const goToNextStep = async () => {
    if (integrationType === 'cicd') {
      setCurrentStep(prev => prev + 1);
      return;
    }

    if (currentStep === 1) {
      // Check if any repo has no branches selected
      const reposWithNoBranches = Object.entries(selectedRepos).filter(([, repo]) => repo.branches.length === 0);
      if (reposWithNoBranches.length > 0) {
        reposWithNoBranches.forEach(([repoId]) => {
          updateBranchValidationMessage(repoId, 'Please select at least one branch');
        });
        return;
      }

      setIsValidatingBranches(true);
      try {
        // In edit mode, only validate branches for Bitbucket
        if (editMode) {
          const res = await axiosHelper.axiosPost(
            'cloudsecurity/iac/repository_branch_authentication',
            {
              platform: repoDetails?.provider,
              repository_id: [
                {
                  id: repoDetails.full_name,
                  branch: selectedRepos[repoDetails.full_name].branches.map(branch => branch.name),
                },
              ],
            },
            true,
          );

          if (res?.data?.data && res?.data?.error === 1) {
            setSelectedRepos(prev => {
              const updatedRepos = { ...prev };
              res.data.data.forEach(repo => {
                if (updatedRepos[repo.repository_name]) {
                  const invalidBranches = repo.invalid_branches || [];
                  updatedRepos[repo.repository_name] = {
                    ...updatedRepos[repo.repository_name],
                    branches: updatedRepos[repo.repository_name].branches.map(branch => ({
                      ...branch,
                      isValid: !invalidBranches.includes(branch.name),
                    })),
                  };
                  // Update validation message for invalid branches
                  if (invalidBranches.length > 0) {
                    updateBranchValidationMessage(
                      repo.repository_name,
                      `Invalid branch names: ${invalidBranches.join(', ')}`,
                    );
                  }
                }
              });
              return updatedRepos;
            });
            setIsValidatingBranches(false);
            return;
          }
          if (res?.data?.error) {
            toast.error('Error authenticating branches');
            setIsValidatingBranches(false);
            return;
          }
        } else {
          // Non-edit mode validation
          const res = await axiosHelper.axiosPost(
            'cloudsecurity/iac/repository_branch_authentication',
            {
              platform: editMode ? repoDetails?.provider : installation_id ? 'github' : 'bitbucket',
              repository_id: Object.entries(selectedRepos).map(([repoId, repo]) => ({
                id: repo.fullName || repoId,
                branch: repo.branches.map(branch => branch.name),
              })),
            },
            true,
          );
          if (res?.data?.data && res?.data?.error === 1) {
            setSelectedRepos(prev => {
              const updatedRepos = { ...prev };
              res.data.data.forEach(repo => {
                if (updatedRepos[repo.repository_name]) {
                  const invalidBranches = repo.invalid_branches || [];
                  updatedRepos[repo.repository_name] = {
                    ...updatedRepos[repo.repository_name],
                    branches: updatedRepos[repo.repository_name].branches.map(branch => ({
                      ...branch,
                      isValid: !invalidBranches.includes(branch.name),
                    })),
                  };
                  // Update validation message for invalid branches
                  if (invalidBranches.length > 0) {
                    updateBranchValidationMessage(
                      repo.repository_name,
                      `Invalid branch names: ${invalidBranches.join(', ')}`,
                    );
                  }
                }
              });
              return updatedRepos;
            });
            setIsValidatingBranches(false);
            return;
          }
          if (res?.data?.error) {
            toast.error('Error authenticating branches');
            setIsValidatingBranches(false);
            return;
          }
        }
        setCurrentStep(currentStep + 1);
      } catch (error) {
        toast.error('Error validating branches');
      } finally {
        setIsValidatingBranches(false);
      }
    }
    if (currentStep === 2) {
      setCurrentStep(currentStep + 1);
    }
    if (currentStep === 3) {
      setIsRunningScan(true);
      const res = await axiosHelper.axiosPost(
        'cloudsecurity/iac/repository_scanning',
        {
          platform: editMode ? repoDetails?.provider : installation_id ? 'github' : 'bitbucket',
          default_compliance: organization.defaultFrameworks,
          compliance_for_repository: Object.entries(selectedRepos)
            .filter(([repoId]) => repoFrameworks[repoId]?.length > 0)
            .map(([repoId, repo]) => ({
              id: repo.fullName || repoId,
              compliance: repoFrameworks[repoId],
            })),
          repository_scan: Object.entries(selectedRepos).map(([repoId, repo]) => repo.fullName || repoId),
        },
        true,
      );
      if (res?.data?.data && res?.data?.error === 1) {
        toast.error('Error while trying to scan repositories');
      } else if (res?.data && res?.data?.success) {
        // toast.success('Repositories scan triggered successfully');
        if (editMode) {
          onClose?.(); // Only close modal after saving in edit mode
        } else {
          history.push('/code/repository-scanning');
        }
      }
      setIsRunningScan(false);
    }
  };

  const handleSaveAndNext = useCallback(async () => {
    try {
      setIsLoadingNextButton(true);
      const res = await axiosHelper.axiosPost(
        'cloudsecurity/iac/bitbucket_pipline_yml',
        {
          provider: editMode ? repoDetails?.provider : installation_id ? 'github' : 'bitbucket',
          alerts: cidcdType,
          alerts_channel: selectedchannel,
          repository_config: groups.map(group => ({
            id: group['repos'],
            Iac_config: group['iac'],
            vuln_config: group['cve'],
            secrets_config: group['secret']?.map(item => item.toLowerCase() + '_secrets'),
          })),
        },
        true,
      );

      if (res?.data) {
        setYamlContent(res.data.toString());
        setCICDPage(prev => prev + 1);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingNextButton(false);
    }
  }, [cidcdType, editMode, groups, installation_id, repoDetails?.provider, selectedchannel]);

  const user = useSelector(state => state.user);

  useEffect(() => {
    (async () => {
      const res = await agent.Integration.channelDetails();

      // check slack integration
      if (!(!isEmpty(res) && Object.keys(res?.data)?.includes('SLACK'))) {
        setIsSlackConfigured(false);
      } else {
        // setSlackID(res?.data[0]?.id);
        setIsSlackConfigured(true);
        if (res?.data?.SLACK.isEnable) {
          setIsSlackEnabled(true);
        } else {
          setIsSlackEnabled(false);
        }
      }

      // check Microsoft Teams integration
      if (!(!isEmpty(res) && Object.keys(res?.data)?.includes('MICROSOFTTEAMS'))) {
        setIsMicrosoftTeamsConfigured(false);
      } else {
        setIsMicrosoftTeamsConfigured(true);
        if (res?.data?.MICROSOFTTEAMS.isEnable) {
          setIsMicrosoftTeamsEnabled(true);
        } else {
          setIsMicrosoftTeamsEnabled(false);
        }
      }

      // check Google chat integration
      if (!(!isEmpty(res) && Object.keys(res?.data)?.includes('GOOGLECHAT'))) {
        setIsGoogleChatConfigured(false);
      } else {
        setIsGoogleChatConfigured(true);
        if (res?.data?.GOOGLECHAT.isEnable) {
          setIsGoogleChatEnabled(true);
        } else {
          setIsGoogleChatEnabled(false);
        }
      }

      // check Discord integration
      if (!(!isEmpty(res) && Object.keys(res?.data)?.includes('DISCORD'))) {
        setIsDiscordConfigured(false);
      } else {
        setIsDiscordConfigured(true);
        if (res?.data?.DISCORD.isEnable) {
          setIsDiscordEnabled(true);
        } else {
          setIsDiscordEnabled(false);
        }
      }

      if (!isEmpty(res) && Object.keys(res?.data)?.includes('INAPP')) {
        if (res?.data?.INAPP.enable) {
          setIsInAppEnabled(true);
          setSelectedChannel(['INAPP']);
        }
      }
    })();
  }, [setSelectedChannel, user.info.user.email]);

  const handleCICDRunScan = useCallback(async () => {
    const res = await axiosHelper.axiosPost(
      'cloudsecurity/iac/repository_scanning',
      {
        platform: editMode ? repoDetails?.provider : installation_id ? 'github' : 'bitbucket',
        default_compliance: organization.defaultFrameworks,
        compliance_for_repository: Object.entries(selectedRepos)
          .filter(([repoId]) => repoFrameworks[repoId]?.length > 0)
          .map(([repoId, repo]) => ({
            id: repo.fullName || repoId,
            compliance: repoFrameworks[repoId],
          })),
        repository_scan: Object.entries(selectedRepos).map(([repoId, repo]) => repo.fullName || repoId),
      },
      true,
    );
    if (res?.data?.data && res?.data?.error === 1) {
      toast.error('Error while trying to scan repositories');
    } else if (res?.data && res?.data?.success) {
      // toast.success('Repositories scan triggered successfully');
      if (editMode) {
        onClose?.(); // Only close modal after saving in edit mode
      } else {
        history.push('/code/repository-scanning');
      }
    }
  }, [
    editMode,
    history,
    installation_id,
    onClose,
    organization.defaultFrameworks,
    repoDetails?.provider,
    repoFrameworks,
    selectedRepos,
  ]);

  const handleIntegrationTypeChange = useCallback(e => {
    setIntegrationType(e.target.value);
    setcicdType('');
  }, []);

  const handleCICDTypeChange = useCallback(e => {
    setcicdType(e.target.value);
  }, []);
  // Navigate to previous step
  const goToPrevStep = useCallback(() => {
    setCurrentStep(prev => prev - 1);
  }, []);

  const handleClose = useCallback(() => {
    history.push('/code/repository-scanning');
  }, [history]);

  // Handle final submit
  // const handleSubmit = () => {
  //   console.log('Final submission with:', {
  //     repositories: selectedRepos,
  //     compliance: selectedCompliance,
  //   });
  //   alert(`Successfully onboarded ${Object.keys(selectedRepos).length} repositories with compliance frameworks!`);
  // };

  const [showSuccessMessage] = useState(false);
  // console.log(organization.defaultFrameworks, 'hjhfih');

  // Add close handler

  // Set initial organization default frameworks from API response
  useEffect(() => {
    // console.log(complianceFramework?.data?.compliance, 'sdbsbdb');
    if (!editMode && complianceFramework?.data?.compliance && Array.isArray(complianceFramework?.data?.compliance)) {
      setOrganization(prev => ({
        ...prev,
        defaultFrameworks: [...new Set(complianceFramework?.data?.compliance)],
      }));
    }
  }, [complianceFramework, editMode]);

  // Add new state for webhook configuration
  const [webhookEnabledRepos, setWebhookEnabledRepos] = useState({});

  // Add function to check if webhook submit is enabled
  const isWebhookSubmitEnabled = () => true;

  // Initialize webhook state when repositories are selected
  useEffect(() => {
    if (currentStep === 2) {
      const initialWebhookState = {};
      if (editMode && repoDetails) {
        // In edit mode, initialize with the repo's webhook_id
        initialWebhookState[repoDetails.full_name] = !!repoDetails.webhook_id;
      } else {
        // In onboarding mode, initialize all selected repos to disabled
        Object.keys(selectedRepos).forEach(repoId => {
          initialWebhookState[repoId] = false;
        });
      }
      setWebhookEnabledRepos(initialWebhookState);
    }
  }, [currentStep, selectedRepos, editMode, repoDetails]);

  useEffect(() => {
    const enabledRepoIds = Object.entries(webhookEnabledRepos)
      .filter(([, enabled]) => enabled)
      .map(([repoId]) => repoId);
    const configureWebhooks = async () => {
      try {
        // Only include repositories that have webhooks enabled

        // console.log('Enabled Repo IDs:', enabledRepoIds);

        if (enabledRepoIds.length === 0) {
          setCurrentStep(currentStep + 1);
          return;
        }

        const res = await axiosHelper.axiosPost(
          'cloudsecurity/iac/bitbucket_repository_webhook',
          {
            platform: editMode ? repoDetails?.provider : installation_id ? 'github' : 'bitbucket',
            repository_id: enabledRepoIds,
          },
          true,
        );

        if (res?.data?.error) {
          toast.error('Error configuring webhooks');
        }
      } catch (error) {
        toast.error('Error configuring webhooks');
      }
    };

    if (enabledRepoIds.length > 0 && webhookEnabledRepos[enabledRepoIds[0]]) {
      configureWebhooks();
    }
  }, [currentStep, editMode, installation_id, repoDetails?.provider, webhookEnabledRepos]);
  // Add function to toggle webhook for a repository
  const toggleWebhook = repoId => {
    if (editMode && repoDetails?.webhook_id) {
      // In edit mode, if webhook_id exists, we need to handle deletion
      if (webhookEnabledRepos[repoId]) {
        // If currently enabled, we'll disable it (delete webhook)
        setWebhookEnabledRepos(prev => ({
          ...prev,
          [repoId]: false,
        }));
      } else {
        // If currently disabled, we'll enable it (create new webhook)
        setWebhookEnabledRepos(prev => ({
          ...prev,
          [repoId]: true,
        }));
      }
    } else {
      // Normal toggle for non-edit mode or when no webhook exists
      setWebhookEnabledRepos(prev => ({
        ...prev,
        [repoId]: !prev[repoId],
      }));
    }
  };
  const repos = useMemo(() => {
    if (isFetchingTableData || isEmpty(selectedRepo)) return [];

    return !editMode
      ? tableData?.data?.repositories?.map(repo => ({
          value: repo.full_name,
          label: repo.full_name,
        }))
      : [{ label: selectedRepo?.fullName, value: selectedRepo?.fullName }];
  }, [editMode, isFetchingTableData, selectedRepo, tableData?.data?.repositories]);

  // Update the webhook list rendering in edit mode
  {
    editMode && (
      <div className="webhook-list">
        {[repoDetails].map(repo => (
          <div key={repo.full_name} className="webhook-item">
            <div className="webhook-repo-info">
              <div className="webhook-repo-name">{repo.name}</div>
              <div className="webhook-branch-count">
                {selectedRepos[repo.full_name]?.branches.length || 0} branch
                {selectedRepos[repo.full_name]?.branches.length !== 1 ? 'es' : ''} selected
              </div>
              {repo.webhook_id && <div className="webhook-id-info">Webhook ID: {repo.webhook_id}</div>}
            </div>
            <div className="webhook-toggle">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={webhookEnabledRepos[repo.full_name]}
                  onChange={() => toggleWebhook(repo.full_name)}
                />
                <span className="slider"></span>
              </label>
              <span className="webhook-status">{webhookEnabledRepos[repo.full_name] ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Add new state for branch validation messages
  const [branchValidationMessages, setBranchValidationMessages] = useState({});

  // Update the branch validation message for a repository
  const updateBranchValidationMessage = (repoId, message) => {
    setBranchValidationMessages(prev => ({
      ...prev,
      [repoId]: message,
    }));
  };

  // Update the branch input field JSX in both edit and non-edit modes
  const renderBranchInput = (repoId, repo) => (
    <div className="branch-input-container">
      <div className="branch-input-wrapper">
        {repo.branches.map(branch => (
          <div key={branch.name} className={`branch-input-tag ${branch.isValid ? 'is-valid' : 'is-invalid'}`}>
            <GitBranch className="branch-tag-icon" />
            <span>{branch.name}</span>
            <button
              onClick={() => removeBranch(repoId, branch.name)}
              className="branch-tag-remove"
              aria-label="Remove branch">
              <X />
            </button>
          </div>
        ))}

        {repo.branches.length < 5 && (
          <div className="branch-input-field">
            <GitBranch className="branch-input-icon" />
            <input
              type="text"
              placeholder={repo.branches.length === 0 ? 'Enter branch names' : 'Add another branch'}
              value={repo.currentInput || ''}
              onChange={e => updateCurrentInput(repoId, e.target.value)}
              onKeyDown={e => handleKeyDown(e, repoId)}
            />
          </div>
        )}
      </div>

      <div className="branch-actions">
        <div className="branch-info">
          <span className={`branch-count ${repo.branches.length >= 5 ? 'max-reached' : ''}`}>
            {repo.branches.length}/5 branches
          </span>

          {repo.branches.length < 5 && <span className="branch-tip">Press Enter to add</span>}

          {repo.currentInput && (
            <button
              onClick={() => addBranchName(repoId, repo.currentInput)}
              disabled={repo.branches.length >= 5}
              className="add-branch-button">
              Add
            </button>
          )}
        </div>
        {branchValidationMessages[repoId] && (
          <div className="branch-validation-message">{branchValidationMessages[repoId]}</div>
        )}
      </div>
    </div>
  );

  // Update the repository list rendering to use the new branch input component
  // In the repository list section, replace the existing branch input with:
  // {isSelected && renderBranchInput(repo.id, selectedRepos[repo.id])}

  // Add these styles to the existing style section
  // const styles = `
  //   // ... existing styles ...
  //   .branch-validation-message {
  //     color: #dc2626;
  //     font-size: 12px;
  //     margin-top: 4px;
  //     padding: 4px 8px;
  //     background-color: #fee2e2;
  //     border-radius: 4px;
  //     border: 1px solid #fecaca;
  //   }
  //   .branch-info {
  //     display: flex;
  //     align-items: center;
  //     gap: 8px;
  //   }
  //   .branch-actions {
  //     display: flex;
  //     flex-direction: column;
  //     gap: 4px;
  //   }
  //   .branch-input-tag.is-invalid {
  //     background-color: #fee2e2;
  //     border: 1px solid #fecaca;
  //     color: #dc2626;
  //   }
  // `;

  // Update the repository list rendering in both edit and non-edit modes
  // For edit mode:
  {
    editMode && selectedRepo && (
      <div className="repo-items">
        <li className="repo-item is-selected">
          <div className="repo-main">
            <div className="repo-selection">
              <label className="repo-label">
                <span className="repo-name">{selectedRepo.repoName}</span>
              </label>
            </div>
            {renderBranchInput(selectedRepo.repoId, selectedRepos[selectedRepo.repoId])}
          </div>
        </li>
      </div>
    );
  }

  // For non-edit mode:
  {
    !editMode &&
      tableData?.data?.repositories?.map(repo => {
        const isSelected = !!selectedRepos[repo.id];
        return (
          <li key={repo.id} className={`repo-item ${isSelected ? 'is-selected' : ''}`}>
            <div className="repo-main">
              <div className="repo-selection">
                <input
                  type="checkbox"
                  id={`repo-${repo.id}`}
                  checked={isSelected}
                  onChange={() => toggleRepoSelection(repo)}
                  className="repo-checkbox"
                />
                <label htmlFor={`repo-${repo.id}`} className="repo-label">
                  <span className="repo-name">{repo.name}</span>
                </label>
              </div>
              {isSelected && renderBranchInput(repo.id, selectedRepos[repo.id])}
            </div>
          </li>
        );
      });
  }

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="iac-onboarding">
          <div className="iac-container">
            <header className="iac-header">
              <div className="header-content">
                <h1>{editMode ? 'Edit Repository Settings' : 'Code Security Onboarding'}</h1>
                <button onClick={onClose || handleClose} className="close-button" aria-label="Close modal">
                  <X size={20} />
                </button>
              </div>
            </header>

            {/* Step 1: Repository Selection */}
            {currentStep === 1 && (
              <div className="">
                <p className="step-description">
                  {editMode
                    ? 'Add or remove branches for the selected repository'
                    : 'Select repositories and specify branch names for code security onboarding'}
                </p>

                {/* Search - Only show in non-edit mode */}
                {!editMode && (
                  <div className="search-container">
                    <div className="search-icon">
                      <Search />
                    </div>
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search repositories..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </div>
                )}

                {/* Loading States - Only show in non-edit mode */}
                {!editMode && !isEnableApi ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Fetching repository details...</p>
                  </div>
                ) : !editMode && isFetchingTableData ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                  </div>
                ) : (
                  <>
                    {/* Repository List */}
                    <div className="repository-list">
                      <div className="repository-header">
                        <div className="header-left">
                          <Server className="section-icon" />
                          <h2>{editMode ? 'Repository Branches' : 'Available Repositories'}</h2>
                        </div>
                        <span className="repo-tip">Select up to 5 branches per repository</span>
                      </div>

                      {editMode ? (
                        // Edit mode repository display
                        <div className="repo-items">
                          {selectedRepo && (
                            <li className="repo-item is-selected">
                              <div className="repo-main">
                                <div className="repo-selection">
                                  <label className="repo-label">
                                    <span className="repo-name">{selectedRepo.repoName}</span>
                                  </label>
                                </div>
                                {renderBranchInput(selectedRepo.repoId, selectedRepos[selectedRepo.repoId])}
                              </div>
                            </li>
                          )}
                        </div>
                      ) : (
                        // Non-edit mode repository list
                        <>
                          {tableData?.data?.total_items === 0 && tableData?.data?.total_repo > 0 ? (
                            <div className="no-repos-message">
                              <Info className="info-icon" />
                              <p>
                                All {tableData.data.total_repo} repositories have been onboarded for code security
                                scanning.
                              </p>
                            </div>
                          ) : tableData?.data?.total_repo > 0 ? (
                            <div className="repo-status-message">
                              <Info className="info-icon" />
                              <p>
                                {tableData.data.total_items} repositories available for onboarding out of{' '}
                                {tableData.data.total_repo} total repositories.
                                {tableData.data.total_repo - tableData.data.total_items} repositories are already
                                onboarded.
                              </p>
                            </div>
                          ) : null}

                          <ul className="repo-items">
                            {tableData?.data?.repositories?.map(repo => {
                              const isSelected = !!selectedRepos[repo.id];
                              return (
                                <li key={repo.id} className={`repo-item ${isSelected ? 'is-selected' : ''}`}>
                                  <div className="repo-main">
                                    <div className="repo-selection">
                                      <input
                                        type="checkbox"
                                        id={`repo-${repo.id}`}
                                        checked={isSelected}
                                        onChange={() => toggleRepoSelection(repo)}
                                        className="repo-checkbox"
                                      />
                                      <label htmlFor={`repo-${repo.id}`} className="repo-label">
                                        <span className="repo-name">{repo.name}</span>
                                        {/* <span className="platform-badge">{repo.platform}</span> */}
                                      </label>
                                    </div>
                                    {isSelected && renderBranchInput(repo.id, selectedRepos[repo.id])}
                                  </div>
                                </li>
                              );
                            })}

                            {tableData?.data?.repositories?.length === 0 && (
                              <li className="no-repos-found">No repositories found</li>
                            )}
                          </ul>
                        </>
                      )}
                    </div>

                    {/* Pagination - Only show in non-edit mode */}
                    {!editMode && (
                      <div className="pagination">
                        <div className="pagination-controls">
                          <button
                            onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                            disabled={currentPage === 1 || isFetchingTableData}
                            className="pagination-button prev"
                            aria-label="Previous page">
                            <ChevronLeft />
                          </button>

                          <span className="page-indicator">
                            Page {currentPage} of {Math.ceil(tableData?.data?.total_items / 5) || 1}
                          </span>

                          <button
                            onClick={() => handlePageChange(Math.min(currentPage + 1, tableData?.data?.total_items))}
                            disabled={
                              currentPage === Math.ceil(tableData?.data?.total_items / 5) ||
                              tableData?.data?.total_items === 0 ||
                              isFetchingTableData
                            }
                            className="pagination-button next"
                            aria-label="Next page">
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Navigation Buttons */}
                <div className="navigation-buttons">
                  <div></div>
                  <button
                    onClick={goToNextStep}
                    disabled={!isRepoSubmitEnabled() || (editMode ? false : isFetchingTableData)}
                    className={`submit-button ${isRepoSubmitEnabled() ? 'enabled' : ''}`}>
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Compliance Selection */}
            {currentStep === 2 && (
              <div className="compliance-selection">
                {/* Main tabs */}
                <div className="main-tabs">
                  <button
                    onClick={() => setActiveTab('organization')}
                    className={activeTab === 'organization' ? 'active' : ''}>
                    <Building className="tab-icon" />
                    Organization Settings
                  </button>
                  <button
                    onClick={() => setActiveTab('repository')}
                    className={activeTab === 'repository' ? 'active' : ''}>
                    <GitBranch className="tab-icon" />
                    Repository Settings
                  </button>
                </div>

                {/* Success Message */}
                {showSuccessMessage && (
                  <div className="success-message">
                    <Check className="success-icon" />
                    <p>Changes saved successfully</p>
                  </div>
                )}

                {/* Organization tab content */}
                {activeTab === 'organization' && (
                  <div className="tab-content">
                    <div className="info-alert">
                      <Info className="info-icon" />
                      <p>
                        {editMode
                          ? 'Organization-level frameworks are currently set to match the repository settings. You can modify these frameworks as needed.'
                          : 'Organization-level frameworks serve as defaults for all repositories. Individual repositories can inherit these settings or configure their own frameworks.'}
                      </p>
                    </div>

                    <div className="content-card">
                      <div className="org-header">
                        <Building className="section-icon" />
                        <h2>Default Security Frameworks</h2>
                      </div>

                      <div className="filter-container">
                        <label>Filter Frameworks</label>
                        <select value={filter} onChange={e => setFilter(e.target.value)} className="filter-select">
                          <option value="all">All Frameworks</option>
                          <option value="recommended">Recommended</option>
                        </select>
                      </div>

                      <div className="frameworks-container">
                        {getFilteredFrameworks().map(framework => (
                          <div key={framework.id} className="framework-card">
                            <div className="framework-checkbox-container">
                              <input
                                type="checkbox"
                                id={`org-framework-${framework.id}`}
                                checked={organization.defaultFrameworks.map(String).includes(String(framework.id))}
                                onChange={() => toggleOrgFramework(framework.id)}
                                className="framework-checkbox"
                              />
                            </div>
                            <label htmlFor={`org-framework-${framework.id}`} className="framework-content">
                              <div className="framework-name-row">
                                <div className="framework-name">{framework.name}</div>
                                {framework.recommended && <div className="recommended-tag">Recommended</div>}
                              </div>
                              <div className="framework-description">{framework.description}</div>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Repository tab content */}
                {activeTab === 'repository' && (
                  <div className="tab-content repo-layout">
                    {/* Repository list */}
                    <div className="repo-list">
                      <div className="content-card">
                        <h2>Repositories</h2>
                        <div className="repo-list-items">
                          {editMode
                            ? [selectedRepo].map(repo => (
                                <div
                                  key={repo?.repoId}
                                  className={`repo-list-item selected`}
                                  onClick={() => setSelectedRepo(repo)}>
                                  <div className="repo-list-content">
                                    <GitBranch className="repo-icon" />
                                    <div className="repo-info">
                                      <div className="repo-name">{repo?.repoName}</div>
                                      <div className="framework-count">
                                        {
                                          [
                                            ...new Set([
                                              ...(organization.defaultFrameworks || []),
                                              ...(repoFrameworks[repo?.repoId] || []),
                                            ]),
                                          ].length
                                        }{' '}
                                        frameworks
                                      </div>
                                    </div>
                                  </div>
                                  <ArrowRight className="arrow-icon" />
                                </div>
                              ))
                            : Object.values(selectedRepos).map(repo => (
                                <div
                                  key={repo.repoId}
                                  className={`repo-list-item ${
                                    selectedRepo && selectedRepo.repoId === repo.repoId ? 'selected' : ''
                                  }`}
                                  onClick={() => setSelectedRepo(repo)}>
                                  <div className="repo-list-content">
                                    <GitBranch className="repo-icon" />
                                    <div className="repo-info">
                                      <div className="repo-name">{repo.repoName}</div>
                                      <div className="framework-count">
                                        {
                                          [
                                            ...new Set([
                                              ...(organization.defaultFrameworks || []),
                                              ...(repoFrameworks[repo.repoId] || []),
                                            ]),
                                          ].length
                                        }{' '}
                                        frameworks
                                      </div>
                                    </div>
                                  </div>
                                  {selectedRepo && selectedRepo.repoId === repo.repoId && (
                                    <ArrowRight className="arrow-icon" />
                                  )}
                                </div>
                              ))}
                        </div>
                      </div>
                    </div>

                    {/* Repository settings */}
                    <div className="repo-settings">
                      {selectedRepo ? (
                        <div className="content-card">
                          <h2>{selectedRepo.repoName}</h2>

                          <div className="repo-frameworks">
                            <div className="org-alert">
                              <AlertCircle className="alert-icon" />
                              <div className="alert-content">
                                <div className="alert-title">Organization Default Frameworks</div>
                                <div className="alert-description">
                                  {editMode
                                    ? 'These frameworks are currently set as organization defaults and can be modified in the Organization Settings tab.'
                                    : 'These frameworks are applied by default and cannot be modified'}
                                </div>
                              </div>
                            </div>

                            <div className="filter-container">
                              <label>Filter Frameworks</label>
                              <select
                                value={filter}
                                onChange={e => setFilter(e.target.value)}
                                className="filter-select">
                                <option value="all">All Frameworks</option>
                                <option value="recommended">Recommended</option>
                              </select>
                            </div>

                            <div className="frameworks-container">
                              {getFilteredFrameworks().map(framework => {
                                const isOrgDefault = organization.defaultFrameworks.includes(framework.id);
                                const isSelected = repoFrameworks[selectedRepo?.repoId]?.includes(framework.id);

                                return (
                                  <div key={framework.id} className="framework-card">
                                    <div className="framework-checkbox-container">
                                      <input
                                        type="checkbox"
                                        id={`repo-framework-${selectedRepo.repoId}-${framework.id}`}
                                        checked={isSelected || isOrgDefault}
                                        onChange={() => toggleRepoFramework(selectedRepo.repoId, framework.id)}
                                        disabled={isOrgDefault && !editMode}
                                        className="framework-checkbox"
                                      />
                                    </div>
                                    <label
                                      htmlFor={`repo-framework-${selectedRepo.repoId}-${framework.id}`}
                                      className="framework-content">
                                      <div className="framework-name-row">
                                        <div className="framework-name">{framework.name}</div>
                                        <div className="framework-badges">
                                          {framework.recommended && <div className="recommended-tag">Recommended</div>}
                                          {isOrgDefault && <div className="org-default-tag">Organization Default</div>}
                                        </div>
                                      </div>
                                      <div className="framework-description">{framework.description}</div>
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="no-repo-selected">
                          <GitBranch className="no-repo-icon" />
                          <h3>Select a repository</h3>
                          <p>Choose a repository to configure its security frameworks</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="navigation-buttons">
                  <button onClick={goToPrevStep} className="back-button">
                    <ArrowLeft className="button-icon" />
                    Previous
                  </button>

                  <button
                    onClick={goToNextStep}
                    disabled={
                      currentStep === 2
                        ? !isComplianceSubmitEnabled()
                        : currentStep === 3
                        ? !isWebhookSubmitEnabled()
                        : !isRepoSubmitEnabled()
                    }
                    className={`submit-button ${
                      currentStep === 2
                        ? isComplianceSubmitEnabled()
                          ? 'enabled'
                          : ''
                        : isRepoSubmitEnabled()
                        ? 'enabled'
                        : ''
                    }`}>
                    {isRunningScan || isValidatingBranches ? <Loader /> : null}
                    {currentStep === 3 ? 'Run Scan' : 'Next'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Webhook Configuration */}
            {currentStep === 3 && (
              <RadioGroup value={integrationType} onChange={handleIntegrationTypeChange}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '30px',
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '30px',
                    }}>
                    <FormControlLabel
                      value={'Manual'}
                      control={<Radio sx={{ padding: '4px' }} />}
                      label={<Typography sx={typographyStyles}>Manual Scan</Typography>}
                      sx={{
                        margin: 0,
                        alignItems: 'center',
                        flex: 1,
                        '.MuiFormControlLabel-label': {
                          display: 'inline-flex',
                          alignItems: 'center',
                          fontFamily: 'Manrope',
                        },
                      }}
                    />
                    <FormControlLabel
                      value={'Webhook'}
                      control={<Radio sx={{ padding: '4px' }} />}
                      label={<Typography sx={typographyStyles}>Webhook</Typography>}
                      sx={{
                        margin: 0,
                        alignItems: 'center',
                        flex: 1,
                        '.MuiFormControlLabel-label': {
                          display: 'inline-flex',
                          alignItems: 'center',
                          fontFamily: 'Manrope',
                        },
                      }}
                    />
                    <FormControlLabel
                      value={'cicd'}
                      control={<Radio sx={{ padding: '4px' }} />}
                      label={<Typography sx={typographyStyles}>CI/CD</Typography>}
                      sx={{
                        margin: 0,
                        alignItems: 'center',
                        flex: 1,
                        '.MuiFormControlLabel-label': {
                          display: 'inline-flex',
                          alignItems: 'center',
                          fontFamily: 'Manrope',
                        },
                      }}
                    />
                  </Box>

                  {integrationType === 'Manual' && (
                    <div className="content-card">
                      <div className="webhook-header">
                        <GitBranch className="section-icon" />
                        <h2>Manual Scan</h2>
                      </div>
                      <div className="info-alert">
                        <Info className="info-icon" />
                        <p>
                          When Manual option is selected, scans will not run automatically. An admin must manually
                          trigger a scan for this repository <br /> whenever needed. This step is optional.
                        </p>
                      </div>
                    </div>
                  )}
                  {integrationType === 'Webhook' ? (
                    <WebhookIntegration
                      editMode={editMode}
                      goToPrevStep={goToPrevStep}
                      repoDetails={repoDetails}
                      goToNextStep={goToNextStep}
                      selectedRepos={selectedRepos}
                      webhookEnabledRepos={webhookEnabledRepos}
                      toggleWebhook={toggleWebhook}
                      isValidatingBranches={isValidatingBranches}
                    />
                  ) : integrationType === 'cicd' ? (
                    <CICDIntegration cidcdType={cidcdType} handleCICDTypeChange={handleCICDTypeChange} />
                  ) : null}
                  <div className="navigation-buttons">
                    <button onClick={goToPrevStep} className="back-button">
                      <ArrowLeft className="button-icon" />
                      Previous
                    </button>
                    <button
                      onClick={goToNextStep}
                      className={`submit-button ${integrationType === 'cicd' && cidcdType.length === 0 ? '' : 'enabled'}
                          
                      }`}
                      disabled={integrationType === 'cicd' && cidcdType.length === 0}>
                      {isValidatingBranches ? <Loader /> : integrationType !== 'cicd' ? 'Run Scan' : 'Next'}
                    </button>
                  </div>
                </Box>
              </RadioGroup>
            )}

            {currentStep === 4 ? (
              ciCICDPage === 1 ? (
                <>
                  <GroupSelect
                    severities={severityOptions}
                    groups={groups}
                    setGroups={setGroups}
                    configs={secretStatusOptions}
                    repos={repos}
                  />

                  <Typography
                    sx={{
                      ...typographyStyles,
                      fontSize: '16px',
                      margin: '15px 0px',
                      marginTop: '30px',
                      fontWeight: 600,
                    }}>
                    2. Channel Selection
                  </Typography>
                  <div className="CreateNotification__ChannelsContainer">
                    {channels?.map((channel, index) => (
                      <div className="CreateNotification__ChannelItem" key={index}>
                        {channel.id === 'INAPP' ? (
                          <>
                            <Switch
                              // checked={checked}
                              // onChange={handleChange}
                              // name="toggle"
                              id={channel.id}
                              key={channel.id}
                              size="small"
                              color="primary"
                              // onChange={() => onChannelSelect(channel.id)}
                              checked={selectedchannel && selectedchannel?.includes(channel.id)}
                              disabled={!isInAppEnabled}
                            />
                            {channels[0].name}
                          </>
                        ) : channel.id === 'SLACK' ? (
                          <Tooltip
                            title={
                              // `Slack is not ${isSlackConfigured ? 'Enabled' : 'Configured'}`
                              isSlackConfigured || isSlackEnabled
                                ? null
                                : `Slack is not ${isSlackConfigured ? 'Enabled' : 'Configured'}`
                            }>
                            <Switch
                              // checked={checked}
                              // onChange={handleChange}
                              // name="toggle"
                              id={channel.id}
                              key={channel.id}
                              size="small"
                              color="primary"
                              onChange={() => onChannelSelect(channel.id)}
                              checked={selectedchannel && selectedchannel?.includes(channel.id)}
                              disabled={!isSlackConfigured || !isSlackEnabled}
                            />
                            {channels[2].name}
                          </Tooltip>
                        ) : channel.id === 'MICROSOFTTEAMS' ? (
                          <Tooltip
                            title={
                              // `Microsoft Teams is not ${isMicrosoftTeamsConfigured ? 'Enabled' : 'Configured'}`
                              isMicrosoftTeamsConfigured || isMicrosoftTeamsEnabled
                                ? null
                                : `Microsoft Teams is not ${isMicrosoftTeamsConfigured ? 'Enabled' : 'Configured'}`
                            }>
                            <div style={{ display: 'flex' }}>
                              <Switch
                                // checked={checked}
                                // onChange={handleChange}
                                // name="toggle"
                                id={channel.id}
                                key={channel.id}
                                size="small"
                                color="primary"
                                onChange={() => onChannelSelect(channel.id)}
                                checked={selectedchannel && selectedchannel?.includes(channel.id)}
                                disabled={!isMicrosoftTeamsConfigured || !isMicrosoftTeamsEnabled}
                              />
                              {channels[5].name}
                            </div>
                          </Tooltip>
                        ) : channel.id === 'GOOGLECHAT' ? (
                          <Tooltip
                            title={
                              // `Google Chat is not ${isGoogleChatConfigured ? 'Enabled' : 'Configured'}`
                              isGoogleChatConfigured || isGoogleChatEnabled
                                ? null
                                : `Google Chat is not ${isGoogleChatConfigured ? 'Enabled' : 'Configured'}`
                            }>
                            <>
                              <Switch
                                // checked={checked}
                                // onChange={handleChange}
                                // name="toggle"
                                id={channel.id}
                                key={channel.id}
                                size="small"
                                color="primary"
                                onChange={() => onChannelSelect(channel.id)}
                                checked={selectedchannel && selectedchannel?.includes(channel.id)}
                                disabled={!isGoogleChatConfigured || !isGoogleChatEnabled}
                              />
                              {channels[6].name}
                            </>
                          </Tooltip>
                        ) : channel.id === 'DISCORD' ? (
                          <Tooltip
                            title={
                              // `Discord is not ${isDiscordConfigured ? 'Enabled' : 'Configured'}`
                              isDiscordConfigured || isDiscordEnabled
                                ? null
                                : `Discord is not ${isDiscordConfigured ? 'Enabled' : 'Configured'}`
                            }>
                            <>
                              <Switch
                                // checked={checked}
                                // onChange={handleChange}
                                // name="toggle"
                                id={channel.id}
                                key={channel.id}
                                size="small"
                                color="primary"
                                onChange={() => onChannelSelect(channel.id)}
                                checked={selectedchannel && selectedchannel?.includes(channel.id)}
                                disabled={!isDiscordConfigured || !isDiscordEnabled}
                              />
                              {channels[7].name}
                            </>
                          </Tooltip>
                        ) : (
                          <>
                            <Switch
                              id={channel.id}
                              key={channel.id}
                              checked={selectedchannel && selectedchannel?.includes(channel.id.toUpperCase())}
                              disabled={channel.id === 'EMAIL' || channel.id === 'INAPP' ? false : true}
                              onChange={() => onChannelSelect(channel.id.toUpperCase())}
                              color="primary"
                              size="small"
                            />
                            {channels?.find(item => item.id === channel.id)?.name}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="navigation-buttons">
                    <button onClick={goToPrevStep} className="back-button">
                      <ArrowLeft className="button-icon" />
                      Previous
                    </button>
                    <button
                      onClick={handleSaveAndNext}
                      disabled={isLoadingNextButton}
                      className={`submit-button enabled`}>
                      {isLoadingNextButton ? (
                        <>
                          <Spinner style={{ marginRight: '4px' }} />
                          Saving..
                        </>
                      ) : (
                        'Save And Next'
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <Typography sx={typographyStyles}>Please place the following script in your pipeline</Typography>
                  <Box sx={{ height: 'max-content', marginTop: '30px' }}>
                    <SimpleYamlViewer yamlContent={yamlContent} isLoading={isLoadingNextButton} />
                    <div className="navigation-buttons">
                      <button onClick={() => setCICDPage(prev => prev - 1)} className="back-button">
                        <ArrowLeft className="button-icon" />
                        Previous
                      </button>

                      <button
                        disabled={isScanRunning}
                        onClick={() => {
                          setIsScanRunning(true);
                          handleCICDRunScan();
                          setIsScanRunning(false);
                        }}
                        className={`submit-button enabled`}>
                        Run Scan
                      </button>
                    </div>
                  </Box>
                </>
              )
            ) : null}
          </div>
        </div>
      </div>

      {/* <style jsx>{`
       
      `}</style> */}
    </div>
  );
};

const WebhookIntegration = ({ editMode, repoDetails, selectedRepos, webhookEnabledRepos, toggleWebhook }) => {
  return (
    <div className="webhook-configuration">
      <p className="step-description">
        {editMode
          ? 'Configure webhook settings for automatic scanning when code is pushed to this repository (optional)'
          : 'Configure webhooks for automatic scanning when code is pushed to the selected repositories (optional)'}
      </p>

      <div className="content-card">
        <div className="webhook-header">
          <GitBranch className="section-icon" />
          <h2>{editMode ? 'Repository Webhook' : 'Repository Webhooks'}</h2>
        </div>

        <div className="info-alert">
          <Info className="info-icon" />
          <p>
            {editMode
              ? 'When webhook is enabled, scans will automatically run when code is pushed to the selected branches. This step is optional.'
              : 'When webhooks are enabled, scans will automatically run when code is pushed to the selected branches. You can enable or disable webhooks for each repository individually. This step is optional.'}
          </p>
        </div>

        <div className="webhook-list">
          {editMode
            ? // Edit mode webhook display
              [repoDetails].map(repo => (
                <div key={repo.full_name} className="webhook-item">
                  <div className="webhook-repo-info">
                    <div className="webhook-repo-name">{repo.name}</div>
                    <div className="webhook-branch-count">
                      {selectedRepos[repo.full_name]?.branches.length || 0} branch
                      {selectedRepos[repo.full_name]?.branches.length !== 1 ? 'es' : ''} selected
                    </div>
                  </div>
                  <div className="webhook-toggle">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={webhookEnabledRepos[repo.full_name]}
                        onChange={() => toggleWebhook(repo.full_name)}
                      />
                      <span className="slider"></span>
                    </label>
                    <span className="webhook-status">
                      {webhookEnabledRepos[repo.full_name] ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              ))
            : // Non-edit mode webhook list
              Object.entries(selectedRepos).map(([repoId, repo]) => (
                <div key={repoId} className="webhook-item">
                  <div className="webhook-repo-info">
                    <div className="webhook-repo-name">{repo.repoName || repo.name}</div>
                    <div className="webhook-branch-count">
                      {(repo.branches || repo.branch || []).length} branch
                      {(repo.branches || repo.branch || []).length !== 1 ? 'es' : ''} selected
                    </div>
                  </div>
                  <div className="webhook-toggle">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={webhookEnabledRepos[repoId]}
                        onChange={() => toggleWebhook(repoId)}
                      />
                      <span className="slider"></span>
                    </label>
                    <span className="webhook-status">{webhookEnabledRepos[repoId] ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </div>
              ))}
        </div>
      </div>
    </div>
  );
};

const CICDIntegration = ({ cidcdType, handleCICDTypeChange }) => {
  return (
    <div className="content-card">
      <div className="webhook-header">
        <Server className="section-icon" />
        <h2>CI/CD</h2>
      </div>
      <div className="info-alert">
        <Info className="info-icon" />
        <p>
          When CI/CD option is selected, scans will be triggered as part of your pipeline execution. You can configure
          the pipeline to <br />
          fail or allow PRs based on scan results.This step is optional.
        </p>
      </div>
      <RadioGroup value={cidcdType} onChange={handleCICDTypeChange}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '15px' }}>
          <Typography sx={typographyStyles}>Please select one of the options</Typography>
          <FormControlLabel
            value="auto_pipeline_fail"
            control={<Radio sx={{ padding: '4px' }} />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <Typography sx={typographyStyles}>Fail pipeline and send alert</Typography>
              </Box>
            }
            sx={{
              margin: 0,
              '.MuiFormControlLabel-label': {
                display: 'inline-flex',
                alignItems: 'center',
                fontFamily: 'Manrope',
                gap: '15px',
              },
            }}
          />
          <FormControlLabel
            value="disallow_pr"
            control={<Radio sx={{ padding: '4px' }} />}
            label={
              <Typography sx={typographyStyles}>
                Receive an alert with scan results and an option to allow/disallow the PR
              </Typography>
            }
            sx={{
              margin: 0,
              '.MuiFormControlLabel-label': {
                display: 'inline-flex',
                alignItems: 'center',
                fontFamily: 'Manrope',
              },
            }}
          />
        </Box>
      </RadioGroup>
    </div>
  );
};
